import React, { useMemo, useState } from 'react';
import { DollarSign } from 'lucide-react';
import { useAdminEmiPayments, useUnifiedCustomers } from '../../lib/workspaceStore';
import { PageSearchBar } from '../../components/common/PageSearchBar';
import { resolveCustomerDisplay } from '../../lib/customerDisplay';
import {
  emiCashFromPayment,
  lateFeeCashFromPayment,
  paymentReceiptTotal,
} from '../../lib/portfolioMetrics';
import { inRange } from '../../lib/dateFilter';
import { useDashboardDetailDates } from '../../lib/useDashboardDetailDates';
import type { ExportRow } from '../../lib/exportReports';
import { DashboardDetailShell, DashboardMetricCard, type DashboardDetailDateProps } from './DashboardDetailShell';

interface CollectedEMIsProps extends DashboardDetailDateProps {
  onClose: () => void;
}

export function CollectedEMIs({ onClose, initialDateRange }: CollectedEMIsProps) {
  const admin = useAdminEmiPayments();
  const customers = useUnifiedCustomers();
  const dr = useDashboardDetailDates(initialDateRange);
  const [query, setQuery] = useState('');

  const rows = useMemo(
    () =>
      [...admin]
        .filter((p) => inRange(p.paidAt, dr.range))
        .sort((a, b) => new Date(b.paidAt).getTime() - new Date(a.paidAt).getTime())
        .map((p) => ({
          id: p.id,
          title: p.facilityTitle,
          customerId: p.customerId,
          recordId: p.recordId,
          customerRef: resolveCustomerDisplay(p.customerId, customers, p.recordId),
          customerName: resolveCustomerDisplay(p.customerId, customers, p.recordId),
          date: p.paidAt,
          amount: emiCashFromPayment(p),
          lateFee: lateFeeCashFromPayment(p),
          receiptTotal: paymentReceiptTotal(p),
          slots: p.facilityPayoff
            ? '—'
            : p.carriedAfter != null
              ? p.carriedAfter > 0
                ? `₹${p.carriedAfter.toLocaleString('en-IN')} due`
                : p.carriedAfter < 0
                  ? `₹${Math.abs(p.carriedAfter).toLocaleString('en-IN')} credit`
                  : 'Clear'
              : '—',
          entry: p.entryType ?? '—',
        })),
    [admin, customers, dr.range]
  );

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter(
      (r) =>
        r.title.toLowerCase().includes(q) ||
        r.customerRef.toLowerCase().includes(q) ||
        String(r.entry).toLowerCase().includes(q)
    );
  }, [rows, query]);

  const totalCollected = useMemo(() => rows.reduce((s, r) => s + r.amount, 0), [rows]);
  const totalLateFees = useMemo(() => rows.reduce((s, r) => s + r.lateFee, 0), [rows]);
  const totalReceipts = useMemo(() => rows.reduce((s, r) => s + r.receiptTotal, 0), [rows]);

  const exportRows: ExportRow[] = useMemo(
    () =>
      filtered.map((r) => {
        const c = customers.find(
          (x) => x.id === r.customerId || x.facilities.some((f) => f.recordId === r.recordId)
        );
        return {
          Customer: c?.name ?? r.customerRef,
          Mobile: r.customerRef,
          Facility: r.title,
          Posted: new Date(r.date).toLocaleString(),
          Receipt: r.receiptTotal,
          EmiCash: r.amount,
          LateFee: r.lateFee,
          BalanceAfter: r.slots,
          EntryType: String(r.entry),
        };
      }),
    [filtered, customers]
  );

  return (
    <DashboardDetailShell
      onClose={onClose}
      title="Collected EMIs"
      subtitle="Admin ledger of posted receipts — filter by date, then export."
      icon={DollarSign}
      iconClassName="text-emerald-700"
      exportTitle="Collected EMIs Report"
      exportFilename="collected-emis.xls"
      exportRows={exportRows}
      dateRange={dr.range}
      datePreset={dr.preset}
      onDateStart={dr.setStart}
      onDateEnd={dr.setEnd}
      onDatePreset={dr.setPreset}
      onDateClear={dr.clear}
    >
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 sm:max-w-2xl">
        <DashboardMetricCard label="EMI cash in period" value={`₹${totalCollected.toLocaleString('en-IN')}`} accent />
        <DashboardMetricCard label="Late fees in period" value={`₹${totalLateFees.toLocaleString('en-IN')}`} />
        <DashboardMetricCard label="Total receipts" value={`₹${totalReceipts.toLocaleString('en-IN')}`} />
        <DashboardMetricCard label="Receipt rows" value={rows.length} />
      </div>

      <PageSearchBar
        id="dash-collected-emis-search"
        value={query}
        onChange={setQuery}
        placeholder="Search facility, mobile, entry type…"
        className="max-w-md"
      />

      <div className="app-card overflow-hidden shadow-sm ring-1 ring-zinc-950/[0.03]">
        <div className="border-b border-zinc-200 bg-zinc-50/90 px-4 py-3">
          <h2 className="text-sm font-semibold text-zinc-900">Receipt ledger</h2>
          <p className="text-xs text-zinc-500">{filtered.length} row{filtered.length === 1 ? '' : 's'}</p>
        </div>
        <div className="app-table-wrap border-0">
          <table className="app-table min-w-full">
            <thead>
              <tr>
                <th>Customer</th>
                <th>Mobile / ID</th>
                <th>Facility</th>
                <th>Posted</th>
                <th className="text-right">EMI cash</th>
                <th className="text-right">Late fee</th>
                <th className="text-right">Total receipt</th>
                <th>Balance after</th>
                <th>Type</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-100">
              {filtered.length === 0 ? (
                <tr>
                  <td colSpan={9} className="py-10 text-center text-sm text-zinc-500">
                    {rows.length === 0 ? 'No receipts in this date range.' : 'No rows match your search.'}
                  </td>
                </tr>
              ) : (
                filtered.map((emi) => {
                  const c = customers.find(
                    (x) => x.id === emi.customerId || x.facilities.some((f) => f.recordId === emi.recordId)
                  );
                  return (
                    <tr key={emi.id} className="transition-colors hover:bg-emerald-50/40">
                      <td className="font-medium text-zinc-800">{c?.name ?? '—'}</td>
                      <td className="whitespace-nowrap font-mono text-xs text-zinc-600">{emi.customerRef}</td>
                      <td className="text-sm text-zinc-700">{emi.title}</td>
                      <td className="whitespace-nowrap text-xs tabular-nums text-zinc-600">
                        {new Date(emi.date).toLocaleString()}
                      </td>
                      <td className="whitespace-nowrap text-right font-semibold tabular-nums text-emerald-800">
                        ₹{emi.amount.toLocaleString('en-IN')}
                      </td>
                      <td className="whitespace-nowrap text-right tabular-nums text-zinc-700">
                        {emi.lateFee > 0 ? `₹${emi.lateFee.toLocaleString('en-IN')}` : '—'}
                      </td>
                      <td className="whitespace-nowrap text-right font-semibold tabular-nums text-emerald-900">
                        ₹{emi.receiptTotal.toLocaleString('en-IN')}
                      </td>
                      <td className="whitespace-nowrap text-sm text-zinc-600">{emi.slots}</td>
                      <td className="whitespace-nowrap text-xs text-zinc-500">{String(emi.entry)}</td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </DashboardDetailShell>
  );
}
